import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminAppSettings, AdminFeatureFlags } from '@ridemaadi/types';
import {
  checkAdminAccess,
  fetchAppSettings,
  fetchCosts,
  fetchDashboard,
  fetchDeployment,
  fetchFeatureFlags,
  fetchHealth,
  fetchLogs,
  fetchMapsUsage,
  fetchNotifications,
  fetchRideOperations,
  fetchRides,
  fetchUsers,
  runDiagnostic,
  saveAppSettings,
  saveFeatureFlags,
  suspendUser,
  unsuspendUser,
} from './api';

const REFRESH_MS = 30_000;

export function useAdminAccess() {
  return useQuery({
    queryKey: ['admin', 'access'],
    queryFn: checkAdminAccess,
    retry: false,
    staleTime: 60_000,
  });
}

export function useAdminDashboard(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: fetchDashboard,
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useRideOperations(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'ride-operations'],
    queryFn: fetchRideOperations,
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useAdminHealth(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'health'],
    queryFn: fetchHealth,
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useAdminUsers(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'users', q],
    queryFn: () => fetchUsers(q || undefined),
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: suspendUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useUnsuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: unsuspendUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useAdminRides(status: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'rides', status ?? 'all'],
    queryFn: () => fetchRides(status),
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useMapsUsage(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'maps'],
    queryFn: fetchMapsUsage,
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useAdminNotifications(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'notifications'],
    queryFn: fetchNotifications,
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useFeatureFlags(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'feature-flags'],
    queryFn: fetchFeatureFlags,
    enabled,
  });
}

export function useSaveFeatureFlags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flags: AdminFeatureFlags) => saveFeatureFlags(flags),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] }),
  });
}

export function useAdminLogs(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'logs'],
    queryFn: fetchLogs,
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useDeployment(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'deployments'],
    queryFn: fetchDeployment,
    enabled,
  });
}

export function useCosts(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'costs'],
    queryFn: fetchCosts,
    enabled,
    refetchInterval: REFRESH_MS,
  });
}

export function useAppSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: fetchAppSettings,
    enabled,
  });
}

export function useSaveAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: AdminAppSettings) => saveAppSettings(settings),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });
}

export function useRunDiagnostic() {
  return useMutation({ mutationFn: runDiagnostic });
}
